import React, { useMemo } from "react";

const Loader = ({ 
  size = 64, 
  className = "", 
  fullScreen = false 
}) => {
  // Generate unique IDs for gradients to avoid conflicts
  const gradientIds = useMemo(() => ({
    gradient1: `gradient-${Math.random().toString(36).substr(2, 9)}`,
    gradient2: `gradient-${Math.random().toString(36).substr(2, 9)}`,
  }), []);

  return (
    <div 
      className={`flex flex-col items-center justify-center ${
        fullScreen ? 'min-h-screen' : 'min-h-[400px]'
      } bg-gradient-to-br from-gray-50 via-white to-gray-50 ${className}`}
    >
      <div className="relative">
        {/* Outer rotating ring with gradient */}
        <div className="absolute inset-0">
          <svg
            className="animate-spin"
            style={{ width: size, height: size }}
            fill="none"
            viewBox="0 0 24 24"
          >
            <defs>
              <linearGradient id={gradientIds.gradient1} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10b981" stopOpacity="1" />
                <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="1" />
              </linearGradient>
            </defs>
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke={`url(#${gradientIds.gradient1})`}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="60 40"
              className="opacity-75"
            />
          </svg>
        </div>

        {/* Inner rotating ring (counter-rotation) */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ 
            width: size, 
            height: size,
          }}
        >
          <svg
            className="spin-reverse"
            style={{ 
              width: size * 0.7, 
              height: size * 0.7,
            }}
            fill="none"
            viewBox="0 0 24 24"
          >
            <defs>
              <linearGradient id={gradientIds.gradient2} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="1" />
                <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="1" />
              </linearGradient>
            </defs>
            <circle
              cx="12"
              cy="12"
              r="8"
              stroke={`url(#${gradientIds.gradient2})`}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="40 30"
              className="opacity-60"
            />
          </svg>
        </div>

        {/* Center pulsing dot */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="rounded-full bg-gradient-to-br from-green-400 via-blue-500 to-purple-500 animate-pulse"
            style={{ 
              width: size * 0.2, 
              height: size * 0.2,
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
            }}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes spin-reverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }
        .animate-spin {
          animation: spin 2s linear infinite;
        }
        .spin-reverse {
          animation: spin-reverse 1.5s linear infinite;
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );
};

export default Loader;

